/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace component-selector */

import { Injectable }          from '@angular/core';

import {
  Router,
  NavigationStart
}                              from '@angular/router';

import {
  Observable,
  Subject
}                              from 'rxjs';

import { filter }              from 'rxjs/operators';

import { Alert, AlertType }    from '../models/alert';


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class AlertService
 *
 * @brief from anywhere in our application we can raise a critical alert message, and views watching for
 * alerts will show the user as needed.  Intended to allow multiple alert messages to be posted in order
 * at the top of the application window, and each can be dismissed, persist across navigation links,
 * or auto-close and/or fade out.
 *
 * "id" here is used to identify streams of alerts and allow filtering, so that the subscribers can
 * pick a stream of alerts to watch/render.
 *
 * Angular tutorial on toaster style alerts:
 * @link https://jasonwatmore.com/post/2019/07/05/angular-8-alert-toaster-notifications
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class AlertService {

  private subject   = new Subject<Alert>();
  private defaultId = 'default-alert';

  /* subscribe to alert; by default the alert straam 'default-alert' */
  onAlert(id = this.defaultId): Observable<Alert> {

    /*
     * to filter by stream, we pass the normal observable through a filter pipe, so that
     * the observable they subscribe to can only get values that match the stream id.
     *
     */

    return this.subject.asObservable().pipe(filter(x => x && x.id === id));
  }

  /** success message, and you can fill in other Alert attributes if needed */
  success(message: string, options?: any) {
    this.alert(new Alert({ ...options, type: AlertType.Success, message }));
  }

  /** error message and you can fill in other Alert attributes if needed */
  error(message: string, options?: any) {
    this.alert(new Alert({ ...options, type: AlertType.Error, message }));
  }

  /** information message and you can fill in other Alert attributes if needed */
  info(message: string, options?: any) {
    this.alert(new Alert({ ...options, type: AlertType.Info, message }));
  }

  /** warning message and you can fill in other Alert attributes if needed */
  warn(message: string, options?: any) {
    this.alert(new Alert({ ...options, type: AlertType.Warning, message }));
  }

  /** raise an alert, if the alert stream id is not provided then 'default-id' is used. */
  alert(alert: Alert) {

    /* if no alert stream id is given, then just use 'default-id' */

    alert.id = alert.id || this.defaultId;

    /* publish the alert */

    this.subject.next(alert);
  }

  /** clear alerts */
  clear(id = this.defaultId) {

    /*
     * we only fill in id so that watching by stream id works, but there is no message, so the
     * watcher knows to clear.
     *
     */

    this.subject.next(new Alert({ id }));
  }
}
